import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { saTodayISO } from '../../../lib/dateUtils';
import { API_URL, auth, errMsg, rand } from '../financeApi';
import { monthName } from './payShared';

// target: { kid, month, payment? } — payment present means correction mode
export const RecordPaymentDialog = ({ target, onClose, token, onDone }) => {
  const payment = target?.payment;
  const [amount, setAmount] = useState('');
  const [datePaid, setDatePaid] = useState(saTodayISO());
  const [method, setMethod] = useState('cash');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!target) return;
    const owed = target.kid?.month?.owed;
    setAmount(payment ? String(payment.amount) : owed ? String(owed) : '');
    setDatePaid(payment?.date_paid || saTodayISO());
    setMethod(payment?.method || 'cash');
    setReason('');
  }, [target, payment]);

  const save = async () => {
    setBusy(true);
    try {
      const res = payment
        ? await axios.put(`${API_URL}/finance/parent-payments/${payment.payment_id}`, { amount: Number(amount), date_paid: datePaid, method, reason }, auth(token))
        : await axios.post(`${API_URL}/finance/parent-payments`, { kid_id: target.kid.kid_id, month: target.month, amount: Number(amount), date_paid: datePaid, method }, auth(token));
      toast.success(res.data.message);
      onClose();
      onDone();
    } catch (e) {
      toast.error(errMsg(e, 'Could not save the payment'));
    } finally {
      setBusy(false);
    }
  };

  const valid = amount !== '' && Number(amount) >= (payment ? 0 : 0.01) && datePaid && (!payment || reason.trim());

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="record-payment-dialog">
        <DialogHeader>
          <DialogTitle>{payment ? 'Correct a payment' : 'Record a payment'}</DialogTitle>
          <DialogDescription>
            {target?.kid?.full_name} · {target ? monthName(target.month) : ''}
            {!payment && target?.kid?.month?.expected > 0 && ` · expected ${rand(target.kid.month.expected)}, still owed ${rand(target.kid.month.owed)}`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="pp-amount">Amount received (R)</Label>
              <Input id="pp-amount" type="number" inputMode="decimal" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pp-amount-input" />
            </div>
            <div>
              <Label htmlFor="pp-date">Date paid</Label>
              <Input id="pp-date" type="date" max={saTodayISO()} value={datePaid} onChange={(e) => setDatePaid(e.target.value)} data-testid="pp-date-input" />
            </div>
          </div>
          <div>
            <Label>How was it paid?</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {[['eft', 'EFT'], ['cash', 'Cash']].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setMethod(v)} data-testid={`pp-method-${v}`}
                  className={`rounded-xl border p-2.5 text-sm transition-colors ${method === v ? 'border-emerald-600 bg-emerald-50 text-emerald-800 font-semibold' : 'hover:bg-muted/40'}`}>{l}</button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Parents may pay by EFT or cash.</p>
          </div>
          {!payment && target && !target.kid?.month?.fee_set && (
            <p className="text-xs rounded-lg bg-amber-50 border border-amber-200 text-amber-900 p-2" data-testid="pp-no-fee-warning">No fee is set for this month, so the whole amount will be kept as credit for next month. Set the fee first if this is wrong.</p>
          )}
          {payment && (
            <div>
              <Label htmlFor="pp-reason">Why are you correcting it?</Label>
              <Textarea id="pp-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Typed R50 instead of R500" data-testid="pp-reason-input" />
              <p className="text-xs text-muted-foreground mt-1">Payments can't be deleted. Set the amount to R0 if it was recorded by mistake.</p>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="pp-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy || !valid} data-testid="pp-save-btn">{busy ? 'Saving…' : payment ? 'Save correction' : 'Save payment'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
